import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { SwitchableDealer } from "../models/dealer-switcher";

const STORAGE_KEY = "dmp.activeDealerId";

@Injectable({ providedIn: "root" })
export class DealerSwitcherService {
    private _switchableDealers$ = new BehaviorSubject<SwitchableDealer[]>([]);
    switchableDealers$ = this._switchableDealers$.asObservable();

    private _activeDealerId$ = new BehaviorSubject<number | null>(null);
    activeDealerId$ = this._activeDealerId$.asObservable();

    //Emesso dopo ogni cambio dealer (usato dai componenti per ricaricare i dati).
    dealerChanged$ = new Subject<void>();

    //Emesso dall'interceptor su 403: l'header lo traduce e mostra il toast.
    dealerError$ = new Subject<string>();

    private _previousDealerId: number | null = null;
    private _storageListenerAdded = false;

    // Inizializzato da AuthService al completamento del login con la lista restituita da GET /dealer/switchable.
    init(dealers: SwitchableDealer[]): void {
        this._switchableDealers$.next(dealers);
        const home = dealers.find((d) => d.isHome);

        if (!dealers.length || !home) {
            this._activeDealerId$.next(null);
            return;
        }

        const restored = this.readFromStorage(dealers, home.id);
        this._activeDealerId$.next(restored);
        localStorage.setItem(STORAGE_KEY, String(restored));

        if (!this._storageListenerAdded) {
            this.listenStorageEvents();
            this._storageListenerAdded = true;
        }
    }

    getActiveDealerId(): number | null {
        return this._activeDealerId$.getValue();
    }

    getActiveDealer(): SwitchableDealer | undefined {
        const id = this.getActiveDealerId();
        return this._switchableDealers$.getValue().find((d) => d.id === id);
    }

    isActiveHome(): boolean {
        return this.getActiveDealer()?.isHome ?? true;
    }

    setActiveDealer(dealer: SwitchableDealer): void {
        this._previousDealerId = this._activeDealerId$.getValue();
        this._activeDealerId$.next(dealer.id);
        localStorage.setItem(STORAGE_KEY, String(dealer.id));
        this.dealerChanged$.next();
    }

    //Ripristina il dealer precedente senza emettere dealerChanged$
    revertToPrevious(): void {
        if (this._previousDealerId === null) return;
        const prev = this._switchableDealers$
            .getValue()
            .find((d) => d.id === this._previousDealerId);
        if (prev) {
            this._activeDealerId$.next(prev.id);
            localStorage.setItem(STORAGE_KEY, String(prev.id));
        }
    }

    //Chiamato dall'interceptor su 403: reimposta il dealer home ed emette l'evento errore.
    resetToHome(): void {
        const home = this._switchableDealers$.getValue().find((d) => d.isHome);
        const newId = home?.id ?? null;
        this._activeDealerId$.next(newId);
        if (newId !== null) {
            localStorage.setItem(STORAGE_KEY, String(newId));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
        this.dealerChanged$.next();
    }

    //Chiamato da AuthService al logout per ripulire lo stato
    clearOnLogout(): void {
        localStorage.removeItem(STORAGE_KEY);
        this._activeDealerId$.next(null);
        this._switchableDealers$.next([]);
    }

    private readFromStorage(dealers: SwitchableDealer[], homeId: number): number {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const id = Number(raw);
                if (!isNaN(id) && dealers.some((d) => d.id === id)) return id;
            }
        } catch {
            localStorage.removeItem(STORAGE_KEY);
        }
        return homeId;
    }

    //Sync multi-tab: aggiorna lo stato quando un'altra tab cambia il dealer
    private listenStorageEvents(): void {
        window.addEventListener("storage", (event) => {
            if (event.key !== STORAGE_KEY) return;
            const dealers = this._switchableDealers$.getValue();
            const home = dealers.find((d) => d.isHome);
            if (!home) return;
            const newId = event.newValue ? Number(event.newValue) : home.id;
            if (!isNaN(newId) && dealers.some((d) => d.id === newId)) {
                this._activeDealerId$.next(newId);
                this.dealerChanged$.next();
            }
        });
    }
}
